// stores/offline/OfflineConfigRegistry.ts

/**
 * Conflict resolution strategies supported by ConflictResolver
 */
export type ConflictStrategy = "client" | "server" | "merge" | "manual";

/**
 * Cache priority levels - affects eviction order when storage is low
 */
export type CachePriority = "critical" | "high" | "normal" | "low";

/**
 * Configuration options for offline-capable stores
 */
export interface OfflineStoreConfig {
  /** Conflict resolution strategy to use */
  strategy: ConflictStrategy;
  /** Maximum number of entities to cache (undefined = unlimited) */
  maxCacheSize?: number;
  /** Cache time-to-live in milliseconds */
  cacheTTL?: number;
  /** Priority level for cache eviction */
  priority?: CachePriority;
  /** Whether to sync on reconnect */
  syncOnReconnect?: boolean;
  /** Custom description for UI display */
  description?: string;
}

/**
 * Store categories with their default configurations
 */
export type StoreCategory =
  | "reference"
  | "transactional"
  | "user"
  | "audit"
  | "documents"
  | "financial"
  | "manufacturing"
  | "stateless";

/**
 * Default configurations by category
 */
const CATEGORY_DEFAULTS: Record<StoreCategory, OfflineStoreConfig> = {
  // Reference/Lookup data - server is source of truth
  reference: {
    strategy: "server",
    priority: "high",
    syncOnReconnect: true,
    description: "Reference data - server wins on conflicts",
  },

  // Transactional data - merge changes to preserve work
  transactional: {
    strategy: "merge",
    priority: "critical",
    syncOnReconnect: true,
    description: "Transactional data - attempts to merge changes",
  },

  // User preferences - user's local settings take priority
  user: {
    strategy: "client",
    priority: "high",
    syncOnReconnect: true,
    description: "User data - local changes take priority",
  },

  // Audit logs - limited cache, server wins
  audit: {
    strategy: "server",
    maxCacheSize: 500,
    priority: "low",
    syncOnReconnect: true,
    description: "Audit logs - limited cache size",
  },

  // Documents - require manual review for conflicts
  documents: {
    strategy: "manual",
    priority: "normal",
    syncOnReconnect: true,
    description: "Documents - manual conflict resolution",
  },

  // Financial data - merge with careful handling
  financial: {
    strategy: "merge",
    priority: "critical",
    syncOnReconnect: true,
    description: "Financial data - careful merge handling",
  },

  // Manufacturing data - server is source of truth
  manufacturing: {
    strategy: "server",
    priority: "normal",
    syncOnReconnect: true,
    description: "Manufacturing data - server wins",
  },

  // Stateless services - offline disabled
  stateless: {
    strategy: "server",
    priority: "low",
    syncOnReconnect: false,
    description: "Stateless services - no offline support",
  },
};

/**
 * Store-to-category mapping for known stores
 */
const STORE_CATEGORY_MAP: Record<string, StoreCategory> = {
  // Reference/Lookup stores
  departments: "reference",
  forms: "reference",
  "labor-types": "reference",
  "tax-rates": "reference",
  plants: "reference",
  "job-types": "reference",
  "work-types": "reference",
  categories: "reference",
  "material-types": "reference",
  "equipment-types": "reference",
  statuses: "reference",
  roles: "reference",
  permissions: "reference",

  // Transactional stores
  quotes: "transactional",
  "sales-orders": "transactional",
  projects: "transactional",
  tasks: "transactional",
  "work-orders": "transactional",
  jobs: "transactional",
  tickets: "transactional",
  "purchase-orders": "transactional",

  // User-related stores
  users: "user",
  "user-preferences": "user",
  settings: "user",
  "notification-settings": "user",

  // Audit/Log stores
  "project-activity": "audit",
  notifications: "audit",
  logs: "audit",
  "audit-logs": "audit",
  history: "audit",

  // Document stores
  "project-documents": "documents",
  attachments: "documents",
  files: "documents",

  // Financial stores
  payments: "financial",
  invoices: "financial",
  bills: "financial",
  "credit-memos": "financial",
  "payment-applications": "financial",
  "ica-invoices": "financial",
  transactions: "financial",

  // Manufacturing stores
  products: "manufacturing",
  inventory: "manufacturing",
  bom: "manufacturing",
  manholes: "manufacturing",
  "manhole-quotes": "manufacturing",

  // Stateless services (no offline)
  "manhole-calculator": "stateless",
  "manhole-quote-calculator": "stateless",
  "manhole-validations": "stateless",
  "manhole-quote-converter": "stateless",
  "manhole-pdf": "stateless",
  "wa-tax-lookup": "stateless",
};

/**
 * Custom store configurations (overrides category defaults)
 */
const STORE_CUSTOM_CONFIGS: Record<string, Partial<OfflineStoreConfig>> = {
  // High-priority transactional stores with larger cache
  quotes: {
    cacheTTL: 30 * 60 * 1000, // 30 minutes
    priority: "critical",
  },
  projects: {
    cacheTTL: 30 * 60 * 1000,
    priority: "critical",
  },

  // Financial stores with manual conflict resolution for safety
  payments: {
    strategy: "manual",
    priority: "critical",
  },
  invoices: {
    strategy: "manual",
    priority: "critical",
  },

  // Audit logs with stricter limits
  "audit-logs": {
    maxCacheSize: 200,
    cacheTTL: 60 * 60 * 1000, // 1 hour
  },

  // Notifications with moderate limits
  notifications: {
    maxCacheSize: 100,
    cacheTTL: 15 * 60 * 1000, // 15 minutes
  },
};

/**
 * Registry for offline store configurations.
 * Provides centralized management of conflict strategies and cache settings.
 */
export class OfflineConfigRegistry {
  private static customConfigs: Map<string, OfflineStoreConfig> = new Map();

  /**
   * Get the configuration for a specific store
   */
  static getConfig(storeName: string): OfflineStoreConfig {
    // Check for runtime custom config first
    if (this.customConfigs.has(storeName)) {
      return this.customConfigs.get(storeName)!;
    }

    // Get category for this store
    const category = STORE_CATEGORY_MAP[storeName] || "transactional";

    // Get category defaults
    const categoryConfig = CATEGORY_DEFAULTS[category];

    // Apply any store-specific overrides
    const customOverrides = STORE_CUSTOM_CONFIGS[storeName] || {};

    return {
      ...categoryConfig,
      ...customOverrides,
    };
  }

  /**
   * Get the conflict strategy for a specific store
   */
  static getConflictStrategy(storeName: string): ConflictStrategy {
    return this.getConfig(storeName).strategy;
  }

  /**
   * Get the cache configuration for a specific store
   */
  static getCacheConfig(storeName: string): {
    maxCacheSize?: number;
    cacheTTL?: number;
    priority: CachePriority;
  } {
    const config = this.getConfig(storeName);
    return {
      maxCacheSize: config.maxCacheSize,
      cacheTTL: config.cacheTTL,
      priority: config.priority || "normal",
    };
  }

  /**
   * Check if a store should have offline enabled
   */
  static isOfflineEnabled(storeName: string): boolean {
    const category = STORE_CATEGORY_MAP[storeName];
    return category !== "stateless";
  }

  /**
   * Check if a store should sync on reconnect
   */
  static shouldSyncOnReconnect(storeName: string): boolean {
    return this.getConfig(storeName).syncOnReconnect !== false;
  }

  /**
   * Get the category for a store
   */
  static getCategory(storeName: string): StoreCategory {
    return STORE_CATEGORY_MAP[storeName] || "transactional";
  }

  /**
   * Register a custom configuration for a store at runtime
   */
  static registerConfig(storeName: string, config: OfflineStoreConfig): void {
    this.customConfigs.set(storeName, config);
  }

  /**
   * Clear a custom runtime configuration
   */
  static clearConfig(storeName: string): void {
    this.customConfigs.delete(storeName);
  }

  /**
   * Get all stores in a specific category
   */
  static getStoresByCategory(category: StoreCategory): string[] {
    return Object.entries(STORE_CATEGORY_MAP)
      .filter(([, cat]) => cat === category)
      .map(([store]) => store);
  }

  /**
   * Get all configured store names
   */
  static getAllConfiguredStores(): string[] {
    return Object.keys(STORE_CATEGORY_MAP);
  }

  /**
   * Get category defaults (useful for debugging/admin UI)
   */
  static getCategoryDefaults(): Record<StoreCategory, OfflineStoreConfig> {
    return { ...CATEGORY_DEFAULTS };
  }

  /**
   * Get all stores that require manual conflict resolution
   */
  static getManualConflictStores(): string[] {
    return Object.entries(STORE_CATEGORY_MAP)
      .filter(([storeName]) => {
        const config = this.getConfig(storeName);
        return config.strategy === "manual";
      })
      .map(([store]) => store);
  }

  /**
   * Get stores sorted by priority (for cache eviction order)
   */
  static getStoresByPriority(): Record<CachePriority, string[]> {
    const result: Record<CachePriority, string[]> = {
      critical: [],
      high: [],
      normal: [],
      low: [],
    };

    for (const storeName of Object.keys(STORE_CATEGORY_MAP)) {
      const config = this.getConfig(storeName);
      const priority = config.priority || "normal";
      result[priority].push(storeName);
    }

    return result;
  }
}
